import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { MessageCircle, Minimize2, Send, Zap, TrendingUp, Brain, AlertTriangle, Crown, Lock, ArrowRight } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase';
import { getTrades, getTodayMessageCount, setMessageCount } from './services/firestoreService';
import { getUserSubscription } from './subscriptionUtils';
import { analyzePatterns } from './utils/patternEngine';

const FREE_DAILY_LIMIT = 3;
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;
const GROQ_MODEL = 'llama-3.3-70b-versatile';
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';

// Firebase callable — routes to functions/index.js groqChat
// Make sure to deploy first: firebase deploy --only functions
const firebaseFunctions = getFunctions(app);
const groqChatFn = httpsCallable(firebaseFunctions, 'groqChat');

const QUICK_QUESTIONS = [
  {
    label: '🎯 Aaj kya karu?',
    prompt: (p, name) => `${name} ki trading patterns dekh ke aaj ke liye specific advice do. Worst time: ${p?.worstHour?.label || 'N/A'}. Aaj ${new Date().toLocaleDateString('en-IN', {weekday:'long'})} hai. Recent P&L: ${p?.totalPnl > 0 ? 'Profitable' : 'Loss mein'}. 2-3 specific points do Hinglish mein. Max 100 words.`,
  },
  {
    label: '⚠️ Meri biggest mistake?',
    prompt: (p, name) => `${name} ki trading data se sabse badi ek mistake identify karo. Exact numbers ke saath batao. Weaknesses: ${JSON.stringify(p?.weaknesses)}. Emotions: ${JSON.stringify(p?.emotionArr?.slice(0,4))}. 50-70 words mein, direct aur honest bolo.`,
  },
  {
    label: '💪 Meri strength kya hai?',
    prompt: (p, name) => `${name} ki trading mein kya achha hai? Strengths: ${JSON.stringify(p?.strengths)}. Best setup: ${p?.bestSetup?.setup} (${p?.bestSetup?.winRate}% win rate). Best symbol: ${p?.bestSymbol?.symbol}. Encourage karo but realistic raho. 60-80 words.`,
  },
  {
    label: '📊 Is hafte kaisa raha?',
    prompt: (p, name) => `${name} ka recent trading ka honest review do. Total P&L: ₹${p?.totalPnl}. Win Rate: ${p?.winRate}%. Best day: ${p?.bestDay?.day}. Worst day: ${p?.worstDay?.day}. 80-100 words mein summary do Hinglish mein.`,
  },
  {
    label: '🛑 Kab trading band karun?',
    prompt: (p, name) => `${name} ke liye specific rules batao ki kab trading band karni chahiye. Worst time: ${p?.worstHour?.label || 'N/A'}. Max consecutive losses: ${p?.maxConsecLoss}. Revenge trades: ${p?.revengeCount}. 3 specific stop rules do with exact triggers.`,
  },
];

const AIAssistant = ({ currentUser, minimized, onToggleMinimize }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'ai',
      message: 'Hello! 👋 मैं आपका AI Performance Coach हूं। मैं आपके trades analyze करके personalized insights दे सकता हूं।',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [input, setInput] = useState('');
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [isTyping, setIsTyping] = useState(false);

  // Message limit state
  const [isPaid, setIsPaid] = useState(null);   // null = loading
  const [msgCount, setMsgCount] = useState(0);  // today's used messages

  const messagesEndRef = useRef(null);

  // Quick action buttons
  const quickActions = [
    { id: 'analyze', label: 'Analyze My Trades', icon: Brain },
    { id: 'mistakes', label: 'Show Mistakes', icon: AlertTriangle },
    { id: 'tips', label: 'Give Tips', icon: Zap },
    { id: 'performance', label: 'Performance Summary', icon: TrendingUp }
  ];

  // Load trades, subscription status, and today's message count
  useEffect(() => {
    // Clear stale data immediately on any user change
    setTrades([]);
    setStats(null);
    setPatterns(null);
    setIsPaid(null);
    setMsgCount(0);
    // Reset chat to just the welcome message
    setMessages([{
      id: 1,
      sender: 'ai',
      message: 'Hello! 👋 मैं आपका AI Performance Coach हूं। मैं आपके trades analyze करके personalized insights दे सकता हूं।',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }]);

    if (currentUser) {
      console.log('[AIAssistant] Loading for user:', currentUser.id);
      const load = async () => {
        const [userTrades, sub, count] = await Promise.all([
          getTrades(currentUser.id),
          getUserSubscription(currentUser.id),
          getTodayMessageCount(currentUser.id).catch(() => 0)
        ]);
        console.log('[AIAssistant] Fetched', userTrades.length, 'trades, sub:', sub?.planId, sub?.status);
        setTrades(userTrades);
        if (userTrades.length > 0) {
          calculateStats(userTrades);
          setPatterns(analyzePatterns(userTrades));
        }
        // Paid = any active subscription that hasn't expired
        const paid = !!(sub && sub.status === 'active' && new Date(sub.endDate) >= new Date());
        console.log('[AIAssistant] isPaid:', paid);
        setIsPaid(paid);
        setMsgCount(count);
      };
      load();
    }
  }, [currentUser]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // isPaid=null means still loading — never block during loading
  // Only block when we know for certain the user is not paid (isPaid===false)
  const limitReached = isPaid === false && msgCount >= FREE_DAILY_LIMIT;

  // ── Statistics ────────────────────────────────────────────────

  const calculateStats = (tradesData) => {
    const totalTrades = tradesData.length;
    const winningTrades = tradesData.filter(t => t.pnl > 0);
    const losingTrades = tradesData.filter(t => t.pnl < 0);
    const totalPnL = tradesData.reduce((sum, t) => sum + t.pnl, 0);
    const winRate = (winningTrades.length / totalTrades) * 100;
    const avgWin = winningTrades.length > 0
      ? winningTrades.reduce((sum, t) => sum + t.pnl, 0) / winningTrades.length : 0;
    const avgLoss = losingTrades.length > 0
      ? losingTrades.reduce((sum, t) => sum + t.pnl, 0) / losingTrades.length : 0;
    const riskReward = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;
    const recentPnL = tradesData.slice(-5).reduce((sum, t) => sum + t.pnl, 0);

    const emotionStats = {};
    tradesData.forEach(trade => {
      if (trade.emotion) {
        if (!emotionStats[trade.emotion]) {
          emotionStats[trade.emotion] = { wins: 0, losses: 0, total: 0 };
        }
        emotionStats[trade.emotion].total++;
        if (trade.pnl > 0) emotionStats[trade.emotion].wins++;
        else emotionStats[trade.emotion].losses++;
      }
    });

    setStats({ totalTrades, winningTrades: winningTrades.length, losingTrades: losingTrades.length, totalPnL, winRate, avgWin, avgLoss, riskReward, recentPnL, emotionStats });
  };

  // ── Groq AI via Firebase Cloud Function ───────────────────────

  // Build the system prompt with pattern-aware context
  const buildSystemPrompt = (currentStats, currentTrades) => {
    const userName = currentUser?.displayName?.split(' ')[0] || 'Trader';
    let prompt = `Tu ${userName} ka personal AI Trading Coach hai. TradeFix AI app ke through Indian stock market mein trade karta hai. Hinglish mein jawab de (Hindi + English mix). Concise aur actionable reh. Bullet points use kar jab multiple points ho.`;

    if (currentTrades && currentTrades.length > 0) {
      const p = analyzePatterns(currentTrades);

      if (p) {
        prompt += `\n\n=== ${userName.toUpperCase()} KA PATTERN ANALYSIS ===`;
        prompt += `\nOverall: P&L ₹${p.totalPnl}, Win Rate ${p.winRate}%, ${p.totalTrades} trades (${p.wins}W/${p.losses}L)`;
        prompt += `\nAvg Win: +₹${p.avgWin} | Avg Loss: -₹${Math.abs(p.avgLoss)}`;
        if (p.worstHour) prompt += `\nWorst time slot: ${p.worstHour.label} (₹${p.worstHour.pnl} loss)`;
        if (p.bestHour)  prompt += `\nBest time slot:  ${p.bestHour.label} (₹${p.bestHour.pnl} profit)`;
        if (p.worstDay)  prompt += `\nWorst day: ${p.worstDay.day} (avg ₹${p.worstDay.avgPnl})`;
        if (p.bestDay)   prompt += `\nBest day:  ${p.bestDay.day} (avg ₹${p.bestDay.avgPnl})`;
        if (p.emotionArr?.length) prompt += `\nEmotions: ${p.emotionArr.map(e => `${e.emotion} ${e.winRate}% WR`).join(', ')}`;
        if (p.bestSetup) prompt += `\nBest setup: ${p.bestSetup.setup} (${p.bestSetup.winRate}% WR)`;
        if (p.bestSymbol) prompt += `\nBest symbol: ${p.bestSymbol.symbol} (₹${p.bestSymbol.pnl})`;
        if (p.maxConsecLoss > 0) prompt += `\nMax consecutive losses: ${p.maxConsecLoss}`;
        if (p.revengeCount > 0) prompt += `\nRevenge trades: ${p.revengeCount}`;
        if (p.weaknesses?.length) prompt += `\nWeaknesses: ${p.weaknesses.join(' | ')}`;
        if (p.strengths?.length)  prompt += `\nStrengths: ${p.strengths.join(' | ')}`;
      }

      // Last 30 individual trades for specific Q&A
      const tradesToShow = currentTrades.slice(-30);
      prompt += `\n\nRecent ${tradesToShow.length} trades:`;
      tradesToShow.forEach((t, i) => {
        const sign = (t.pnl || 0) >= 0 ? '+' : '';
        prompt += `\n${i+1}. ${t.date} ${t.stock || t.symbol} ${t.type} ${sign}₹${(t.pnl||0).toFixed(0)} [${t.emotion||'—'}]${t.notes ? ' '+t.notes : ''}`;
      });
    } else {
      prompt += '\n\nUser ne abhi koi trade log nahi kiya. Unhe "My Trades" section mein trades add karne ke liye encourage karo.';
    }

    return prompt;
  };

  // Compute stats inline from a trade list (used when state stats may be stale)
  const computeStatsFromTrades = (tradeList) => {
    if (!tradeList || tradeList.length === 0) return null;
    const wins = tradeList.filter(t => t.pnl > 0);
    const losses = tradeList.filter(t => t.pnl < 0);
    const totalPnL = tradeList.reduce((s, t) => s + t.pnl, 0);
    const winRate = (wins.length / tradeList.length) * 100;
    const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0;
    const riskReward = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;
    const recentPnL = tradeList.slice(-5).reduce((s, t) => s + t.pnl, 0);
    const emotionStats = {};
    tradeList.forEach(t => {
      if (t.emotion) {
        if (!emotionStats[t.emotion]) emotionStats[t.emotion] = { wins: 0, losses: 0, total: 0 };
        emotionStats[t.emotion].total++;
        if (t.pnl > 0) emotionStats[t.emotion].wins++;
        else emotionStats[t.emotion].losses++;
      }
    });
    return { totalTrades: tradeList.length, winningTrades: wins.length, losingTrades: losses.length, totalPnL, winRate, avgWin, avgLoss, riskReward, recentPnL, emotionStats };
  };

  // Build the full messages array for Groq: [system, ...history, newUserMessage]
  const buildGroqMessages = (userMessage, currentMessages, currentStats, currentTrades) => {
    const systemMsg = { role: 'system', content: buildSystemPrompt(currentStats, currentTrades) };

    // Include last 8 messages as conversation history for context
    const history = currentMessages.slice(-8).map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.message
    }));

    return [systemMsg, ...history, { role: 'user', content: userMessage }];
  };

  // ── Send message ──────────────────────────────────────────────

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend) return;
    if (limitReached) return;

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      message: textToSend,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Increment count only for confirmed free users (not during loading)
    if (isPaid === false) {
      const newCount = msgCount + 1;
      setMsgCount(newCount);
      await setMessageCount(currentUser.id, newCount).catch(() => {});
    }

    // ── Always fetch fresh trades before building the prompt ──────────────
    // This fixes stale-state race conditions where trades haven't loaded yet
    let freshTrades = trades;
    let freshStats = stats;
    try {
      if (currentUser) {
        const fetched = await getTrades(currentUser.id);
        console.log('[AIAssistant] Fresh fetch:', fetched.length, 'trades for prompt');
        freshTrades = fetched;
        freshStats = computeStatsFromTrades(fetched);
        // Keep state in sync
        if (fetched.length !== trades.length) {
          setTrades(fetched);
          if (fetched.length > 0) {
            calculateStats(fetched);
            setPatterns(analyzePatterns(fetched));
          }
        }
      }
    } catch (fetchErr) {
      console.warn('[AIAssistant] Trade refresh failed (using cached):', fetchErr.message);
      // Fall back to cached trades — don't block the send
    }

    let reply = null;

    // 1️⃣ Try Firebase Cloud Function first
    try {
      const groqMessages = buildGroqMessages(textToSend, messages, freshStats, freshTrades);
      const result = await groqChatFn({ messages: groqMessages, userId: currentUser.id });
      reply = result.data?.reply;
      console.log('[AIAssistant] Firebase Function succeeded');
    } catch (fnErr) {
      console.warn('[AIAssistant] Firebase Function failed:', fnErr.code, fnErr.message);
    }

    // 2️⃣ Fallback: call Groq API directly from browser
    if (!reply && GROQ_API_KEY) {
      try {
        const groqMessages = buildGroqMessages(textToSend, messages, freshStats, freshTrades);
        const response = await fetch(GROQ_API_URL, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${GROQ_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: GROQ_MODEL,
            messages: groqMessages,
            max_tokens: 1024,
            temperature: 0.7
          })
        });
        const data = await response.json();
        if (response.ok) {
          reply = data.choices?.[0]?.message?.content;
          console.log('[AIAssistant] Direct Groq API succeeded');
        } else {
          console.error('[AIAssistant] Direct Groq API error:', data.error);
        }
      } catch (directErr) {
        console.error('[AIAssistant] Direct Groq call failed:', directErr.message);
      }
    }

    const aiMessage = {
      id: Date.now() + 1,
      sender: 'ai',
      message: reply || '⚠️ Something went wrong, please try again. अगर problem continues तो page refresh करें।',
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, aiMessage]);
    setIsTyping(false);
  };

  const handleQuickAction = async (actionId) => {
    if (limitReached) return;

    // Fetch trade count first to give contextual prompts
    let tradeCount = trades.length;
    if (tradeCount === 0 && currentUser) {
      try {
        const fetched = await getTrades(currentUser.id);
        tradeCount = fetched.length;
        if (fetched.length > 0) { setTrades(fetched); calculateStats(fetched); }
      } catch (_) {}
    }

    if (tradeCount === 0) {
      const noTradesMsg = {
        id: Date.now(),
        sender: 'ai',
        message: '📭 No trades logged yet! Pehle "My Trades" section mein apne trades add karein, phir main aapko personalized analysis de sakta hoon. 😊',
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, noTradesMsg]);
      return;
    }

    const map = {
      analyze: `Analyze all my ${tradeCount} trades in detail. Find patterns, identify my strengths and weaknesses, and tell me which stocks and setups work best for me.`,
      mistakes: `Review my ${tradeCount} trades and identify all emotional and trading mistakes. Show which emotions led to losses, how many revenge/FOMO trades I took, and what I should stop doing.`,
      tips: `Based on my ${tradeCount} trades, give me 5 specific and actionable tips to improve my trading performance. Make them personalized to my actual data.`,
      performance: `Give me a complete performance summary of all my ${tradeCount} trades — total P&L, win rate, best/worst trades, best/worst stock, emotion analysis, and an overall grade.`
    };
    if (map[actionId]) handleSend(map[actionId]);
  };

  // ── Progress bar helpers ──────────────────────────────────────

  const progressColor = () => {
    if (msgCount >= FREE_DAILY_LIMIT) return 'bg-red-500';
    if (msgCount >= FREE_DAILY_LIMIT - 1) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const progressWidth = () =>
    `${Math.min((msgCount / FREE_DAILY_LIMIT) * 100, 100)}%`;

  // ── Minimized bubble ──────────────────────────────────────────

  if (minimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 p-4 rounded-2xl z-50 flex items-center space-x-2 transition-all"
        style={{ background: 'linear-gradient(135deg, #22C55E, #34D399)', boxShadow: '0 8px 32px rgba(34,197,94,0.35)', color: 'var(--bg-card)' }}
      >
        <MessageCircle size={22} />
        <span className="font-semibold text-sm">AI Assistant</span>
        {limitReached ? (
          <div className="w-2.5 h-2.5 bg-red-400 rounded-full" />
        ) : (
          <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse-dot" />
        )}
      </button>
    );
  }

  // ── Expanded view ─────────────────────────────────────────────

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-96 max-w-full sm:rounded-2xl z-50 flex flex-col ai-chat-window" style={{ height: '70vh', maxHeight: '100dvh', background: 'var(--bg-card)', border: '1px solid var(--border-card)', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>

      {/* Header */}
      <div className="text-white p-4 sm:rounded-t-2xl flex items-center justify-between flex-shrink-0" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border-card)' }}>
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)' }}>
            <MessageCircle size={16} className="text-blue-400" />
          </div>
          <div>
            <p className="font-bold text-sm" style={{ letterSpacing: '-0.02em' }}>AI Trading Coach</p>
            <p className="text-xs" style={{ color: patterns ? 'var(--accent)' : '#9CA3AF' }}>
              {patterns ? `${trades.length} trades analyzed ✓` : 'Powered by TradeFix AI'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isPaid === false ? (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.1)', color: '#9CA3AF' }}>
              {msgCount}/{FREE_DAILY_LIMIT} today
            </span>
          ) : isPaid === true ? (
            <span className="flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(234,179,8,0.15)', color: '#FCD34D', border: '1px solid rgba(234,179,8,0.3)' }}>
              <Crown size={10} />
              <span>Pro</span>
            </span>
          ) : null}
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse-dot" />
          <button
            onClick={onToggleMinimize}
            className="text-white/60 hover:text-white transition-colors"
            title="Minimize"
          >
            <Minimize2 size={18} />
          </button>
        </div>
      </div>

      {/* Smart Quick Questions */}
      {patterns && messages.length <= 1 && (
        <div className="p-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--border-card)', background: 'var(--bg-primary)' }}>
          <p className="text-xs mb-2" style={{ color: '#9CA3AF' }}>Quick questions:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {QUICK_QUESTIONS.map((q, i) => {
              const userName = currentUser?.displayName?.split(' ')[0] || 'Trader';
              return (
                <button
                  key={i}
                  onClick={() => !limitReached && handleSend(q.prompt(patterns, userName))}
                  disabled={limitReached}
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-card)',
                    borderRadius: '100px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    color: limitReached ? '#4B5563' : '#93C5FD',
                    cursor: limitReached ? 'not-allowed' : 'pointer',
                  }}
                >
                  {q.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: 'var(--bg-primary)' }}>
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[85%] p-3 rounded-xl"
            style={msg.sender === 'user'
              ? { background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-card)' }
              : { background: 'var(--bg-card)', border: '1px solid var(--border-card)', color: 'var(--text-primary)' }
            }>
              {msg.sender === 'ai' ? (
                <div className="text-sm markdown-content">
                  <ReactMarkdown>{msg.message}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-line">{msg.message}</p>
              )}
              <p className="text-xs mt-1 opacity-70">{msg.time}</p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
              <div className="flex space-x-1.5">
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#3B82F6' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#3B82F6', animationDelay: '0.2s' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#3B82F6', animationDelay: '0.4s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area — switches between normal, usage bar, and limit-reached prompt */}
      <div className="flex-shrink-0 sm:rounded-b-2xl overflow-hidden" style={{ borderTop: '1px solid var(--border-card)' }}>

        {limitReached ? (
          /* ── Limit reached: upgrade prompt ── */
          <div className="p-4" style={{ background: 'var(--bg-card)' }}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <Lock size={16} className="text-red-400" />
              </div>
              <div>
                <p className="font-bold text-white text-sm">Daily Limit Reached!</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Upgrade to Pro for unlimited AI conversations</p>
              </div>
            </div>
            <div className="w-full rounded-full h-1.5 mb-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-1.5 rounded-full w-full" style={{ background: 'linear-gradient(90deg, #EF4444, #F87171)' }} />
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-red-400">3/3 messages used</span>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Resets tomorrow</span>
            </div>
            <button
              onClick={() => {
                onToggleMinimize();
                window.dispatchEvent(new CustomEvent('tradefix-navigate', { detail: 'pricing' }));
              }}
              className="w-full btn-primary flex items-center justify-center space-x-2 py-2.5 rounded-xl text-sm font-bold transition-all"
            >
              <Crown size={14} className="text-yellow-300" />
              <span>Upgrade to Pro — ₹999/mo</span>
              <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          /* ── Normal input ── */
          <div className="p-3">
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask me anything..."
                className="flex-1 p-2.5 rounded-xl text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-card)', color: 'var(--text-primary)' }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="text-white p-2.5 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #22C55E, #34D399)', boxShadow: input.trim() ? '0 4px 12px rgba(34,197,94,0.3)' : 'none', color: 'var(--bg-card)' }}
              >
                <Send size={18} />
              </button>
            </div>

            {/* Usage indicator for confirmed free users only */}
            {isPaid === false && (
              <div className="mt-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">
                    {msgCount}/{FREE_DAILY_LIMIT} messages used today
                  </span>
                  <span className={`text-xs font-semibold ${
                    msgCount >= FREE_DAILY_LIMIT - 1 ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {FREE_DAILY_LIMIT - msgCount} left
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${progressColor()}`}
                    style={{ width: progressWidth() }}
                  />
                </div>
              </div>
            )}

            {/* Pro badge or hint */}
            {isPaid ? (
              <p className="text-xs text-center mt-2 text-purple-400 font-medium flex items-center justify-center space-x-1">
                <Crown size={10} className="text-yellow-500" />
                <span>Pro — Unlimited messages</span>
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1 text-center">
                Try: "Analyze my trades" या "Show mistakes"
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
